/**
 * CommunityTips Component
 *
 * Displays validated community notes as tips or warnings.
 * Used in checklists and compliance checks to provide
 * community-sourced insights clearly labeled as such.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Users,
  Lightbulb,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ThumbsUp,
  CheckCheck,
  ShieldCheck,
  Clock,
  MessageSquare,
} from 'lucide-react';
import { useCommunityTips, type CommunityTip } from '@/hooks/useInspections';
import type { InspectorNoteType } from '@/types/inspection';
import { cn } from '@/lib/utils';

interface CommunityTipsProps {
  jurisdictionId: number | string | null;
  trade?: string;
  context: 'checklist' | 'compliance';
  className?: string;
  defaultExpanded?: boolean;
  limit?: number;
}

// Get icon for note type
function getNoteTypeIcon(noteType: InspectorNoteType) {
  switch (noteType) {
    case 'fail_reason':
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case 'pattern':
      return <Clock className="h-4 w-4 text-blue-500" />;
    case 'preference':
      return <MessageSquare className="h-4 w-4 text-cyan-500" />;
    default:
      return <Lightbulb className="h-4 w-4 text-yellow-500" />;
  }
}

// Get label for note type
function getNoteTypeLabel(noteType: InspectorNoteType): string {
  const labels: Record<string, string> = {
    tip: 'Tip',
    pattern: 'Pattern',
    fail_reason: 'Common Issue',
    preference: 'Inspector Preference',
    schedule: 'Schedule Info',
  };
  return labels[noteType] || noteType;
}

// Single tip item
function CommunityTipItem({ tip, variant }: { tip: CommunityTip; variant: 'tip' | 'warning' }) {
  const isWarning = variant === 'warning' || tip.noteType === 'fail_reason';

  return (
    <div
      className={cn(
        'p-3 rounded-lg border',
        isWarning
          ? 'bg-amber-50/50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800'
          : 'bg-blue-50/50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800'
      )}
    >
      <div className="flex items-start gap-2">
        {getNoteTypeIcon(tip.noteType)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge
              variant={isWarning ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {getNoteTypeLabel(tip.noteType)}
            </Badge>
            {tip.validationState === 'validated' && (
              <Badge variant="outline" className="text-xs gap-1 text-green-600 border-green-300">
                <ShieldCheck className="h-3 w-3" />
                Verified
              </Badge>
            )}
          </div>

          <p className="text-sm leading-relaxed">{tip.content}</p>

          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1 cursor-help">
                    <Users className="h-3 w-3" />
                    {tip.contributorLabel}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Source: Community contributor</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {tip.upvoteCount > 0 && (
              <span className="flex items-center gap-1">
                <ThumbsUp className="h-3 w-3" />
                {tip.upvoteCount}
              </span>
            )}

            {tip.sameHereCount > 0 && (
              <span className="flex items-center gap-1">
                <CheckCheck className="h-3 w-3" />
                {tip.sameHereCount} confirmed
              </span>
            )}

            {tip.confidenceScore > 0 && (
              <span className="text-muted-foreground">
                {Math.round(tip.confidenceScore * 100)}% confidence
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CommunityTips({
  jurisdictionId,
  trade,
  context,
  className,
  defaultExpanded = true,
  limit = 5,
}: CommunityTipsProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const { data: tips, isLoading, error } = useCommunityTips({
    jurisdictionId,
    trade,
    context,
    limit,
    minConfidence: 0.5,
  });

  // Don't render if no tips or error
  if (isLoading || error || !tips || tips.length === 0) {
    return null;
  }

  const variant = context === 'compliance' ? 'warning' : 'tip';
  const title = context === 'compliance' ? 'Community Insights' : 'Community Tips';
  const icon = context === 'compliance' ? (
    <AlertTriangle className="h-4 w-4 text-amber-500" />
  ) : (
    <Lightbulb className="h-4 w-4 text-yellow-500" />
  );

  // Count warnings (fail_reason type)
  const warningCount = tips.filter(t => t.noteType === 'fail_reason').length;

  return (
    <Card className={cn('border-dashed', className)}>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CardHeader className="pb-2">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between p-0 h-auto hover:bg-transparent"
            >
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                {icon}
                {title}
                <Badge variant="outline" className="ml-1 text-xs">
                  {tips.length}
                </Badge>
                {warningCount > 0 && context === 'compliance' && (
                  <Badge variant="destructive" className="ml-1 text-xs">
                    {warningCount} warnings
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                >
                  <Users className="h-3 w-3 mr-1" />
                  Community Sourced
                </Badge>
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
            </Button>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-2 space-y-2">
            <p className="text-xs text-muted-foreground mb-3">
              These insights are contributed by fellow contractors and may not reflect official requirements.
              Always verify with your jurisdiction.
            </p>

            {tips.map((tip) => (
              <CommunityTipItem key={tip.id} tip={tip} variant={variant} />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default CommunityTips;
